import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Camera, Save, Loader } from 'lucide-react';

const ProfileView = () => {
  const { user, getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    curriculum: '',
    grade: '',
    about_me: '',
    avatar_url: '',
    address: '',
    qualification: '',
    bio: '',
    board: ''
  });

  // Fetch user profile from backend on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        setFetching(true);
        const token = getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/profile`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const userData = await response.json();
          setFormData({
            full_name: userData.full_name || user.name || '',
            email: userData.email || '',
            phone: userData.phone || '',
            curriculum: userData.curriculum || '',
            grade: userData.grade || '',
            about_me: userData.about_me || '',
            avatar_url: userData.avatar_url || '',
            address: userData.address || '',
            qualification: userData.qualification || '',
            bio: userData.bio || '',
            board: userData.board || ''
          });
        } else {
          // Fallback to user object from context if API fails
          setFormData({
            full_name: user.full_name || user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            curriculum: user.curriculum || '',
            grade: user.grade || '',
            about_me: user.about_me || '',
            avatar_url: user.avatar_url || '',
            address: user.address || '',
            qualification: user.qualification || '',
            bio: user.bio || '',
            board: user.board || ''
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        // Fallback to user object from context
        setFormData({
          full_name: user.full_name || user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          curriculum: user.curriculum || '',
          grade: user.grade || '',
          about_me: user.about_me || '',
          avatar_url: user.avatar_url || '',
          address: user.address || '',
          qualification: user.qualification || '',
          bio: user.bio || '',
          board: user.board || ''
        });
      } finally {
        setFetching(false);
      }
    };

    fetchProfile();
  }, [user, getAccessToken]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const token = getAccessToken();
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/profile/upload-photo`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, avatar_url: data.avatar_url });
        alert('Profile photo uploaded successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to upload photo: ${error.detail}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getAccessToken();
      
      const updateData = {
        full_name: formData.full_name,
        phone: formData.phone || null,
        address: formData.address || null
      };

      if (user.role === 'student') {
        updateData.curriculum = formData.curriculum || null;
        updateData.grade = formData.grade || null;
        updateData.board = formData.board || null;
      } else if (user.role === 'parent') {
        updateData.grade = formData.grade || null;
        updateData.board = formData.board || null;
      } else if (user.role === 'tutor') {
        updateData.about_me = formData.about_me || null;
        updateData.qualification = formData.qualification || null;
        updateData.bio = formData.bio || null;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/profile`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (response.ok) {
        const updatedUserData = await response.json();
        
        // Update localStorage with the new user data
        const currentUser = JSON.parse(localStorage.getItem('fourq_user') || '{}');
        const mergedUser = {
          ...currentUser,
          name: updatedUserData.full_name,
          full_name: updatedUserData.full_name,
          phone: updatedUserData.phone,
          address: updatedUserData.address,
          curriculum: updatedUserData.curriculum,
          grade: updatedUserData.grade,
          about_me: updatedUserData.about_me,
          avatar_url: updatedUserData.avatar_url,
          qualification: updatedUserData.qualification,
          bio: updatedUserData.bio,
          board: updatedUserData.board
        };
        localStorage.setItem('fourq_user', JSON.stringify(mergedUser));
        
        // Update the local form data to reflect the saved changes
        setFormData({
          ...formData,
          ...updatedUserData
        });
        
        alert('Profile updated successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to update profile: ${error.detail}`);
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">My Profile</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Photo */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              {formData.avatar_url ? (
                <img
                  src={formData.avatar_url}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-cyan-500"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-4xl font-bold">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
              )}
              
              {uploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <Loader className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors">
                  <Camera className="w-5 h-5" />
                  <span>Change Photo</span>
                </div>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                JPG, PNG or WebP. Max 5MB.
              </p>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Full Name
              </label>
              <Input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <Input
                type="email"
                value={formData.email}
                disabled
                className="bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Phone
              </label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Role
              </label>
              <Input
                type="text"
                value={user?.role || ''}
                disabled
                className="bg-gray-100 dark:bg-gray-700 cursor-not-allowed capitalize"
              />
            </div>
          </div>

          {/* Address field for all users */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows="2"
              placeholder="Enter your full address..."
            />
          </div>

          {/* Student-specific fields */}
          {user?.role === 'student' && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Academic Information</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Board
                  </label>
                  <select
                    value={formData.board}
                    onChange={(e) => setFormData({...formData, board: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select board...</option>
                    <option value="CBSE">CBSE</option>
                    <option value="ICSE">ICSE</option>
                    <option value="IGCSE">IGCSE</option>
                    <option value="IB">IB</option>
                    <option value="Others">Others</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Grade
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({...formData, grade: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select grade...</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={`${i + 1}`}>Grade {i + 1}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Curriculum
                  </label>
                  <select
                    value={formData.curriculum}
                    onChange={(e) => setFormData({...formData, curriculum: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select curriculum...</option>
                    <option value="CBSE">CBSE</option>
                    <option value="ICSE">ICSE</option>
                    <option value="IB">IB</option>
                    <option value="State Board">State Board</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Parent-specific fields */}
          {user?.role === 'parent' && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Child Information</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Child's Board
                  </label>
                  <select
                    value={formData.board}
                    onChange={(e) => setFormData({...formData, board: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select board...</option>
                    <option value="CBSE">CBSE</option>
                    <option value="ICSE">ICSE</option>
                    <option value="IGCSE">IGCSE</option>
                    <option value="IB">IB</option>
                    <option value="Others">Others</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Child's Grade
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({...formData, grade: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select grade...</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={`${i + 1}`}>Grade {i + 1}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Tutor-specific fields */}
          {user?.role === 'tutor' && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Professional Information</h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Qualification
                </label>
                <Input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => setFormData({...formData, qualification: e.target.value})}
                  placeholder="e.g., B.Ed in Mathematics, M.Sc Physics..."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Short Bio
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="4"
                  placeholder="Tell students about yourself, your teaching experience, and expertise..."
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="submit"
              disabled={loading}
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-8"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileView;
